import { BlendMode } from 'js/types';

const SHADER_MODE_ADD = 0;
const SHADER_MODE_SUBTRACT = 1;
const SOURCE_TEXTURE_UNIT = 0;
const BACKDROP_TEXTURE_UNIT = 1;
const BASE_MIPMAP_LEVEL = 0;
const POSITION_COMPONENT_COUNT = 2;
const FIRST_VERTEX = 0;
const QUAD_VERTEX_COUNT = 4;
const PACKED_VERTEX_STRIDE_BYTES = 0;
const POSITION_OFFSET_BYTES = 0;

// Two triangles cover WebGL's clip space (-1 to +1 on each axis).
const FULLSCREEN_QUAD_VERTICES = new Float32Array([-1, -1, 1, -1, -1, 1, 1, 1]);

const vertexSource = `
attribute vec2 position;
varying vec2 uv;
const float CLIP_TO_UV_SCALE = 0.5;
void main() {
  // Map clip-space positions [-1, 1] to texture coordinates [0, 1].
  uv = (position + 1.0) * CLIP_TO_UV_SCALE;
  gl_Position = vec4(position, 0.0, 1.0);
}`;

const fragmentSource = `
precision highp float;
uniform sampler2D source;
uniform sampler2D backdrop;
uniform int mode;
varying vec2 uv;
const int MODE_ADD = ${SHADER_MODE_ADD};
const vec3 MIN_COLOR = vec3(0.0);
const vec3 MAX_COLOR = vec3(1.0);
void main() {
  vec4 s = texture2D(source, uv);
  vec4 d = texture2D(backdrop, uv);
  vec3 blended;
  if (mode == MODE_ADD) blended = min(MAX_COLOR, d.rgb + s.rgb);
  else blended = max(MIN_COLOR, d.rgb - s.rgb);
  // Transparent backdrop pixels contribute source color rather than a blend.
  vec3 color = mix(s.rgb, blended, d.a);
  // The WebGL output canvas expects premultiplied alpha.
  gl_FragColor = vec4(color * s.a, s.a);
}`;

/**
 * Blends cropped source/backdrop textures without synchronous CPU pixel readback.
 * One instance reuses its GPU resources across elements and thumbnail captures.
 */
export default class BlendModeCompositor {
  private readonly canvas = document.createElement('canvas');
  private readonly backdrop = document.createElement('canvas');
  private readonly gl = this.canvas.getContext('webgl', {
    alpha: true,
    premultipliedAlpha: true,
    depth: false,
    stencil: false,
    antialias: false
  });
  private program?: WebGLProgram;
  private textures: WebGLTexture[] = [];
  private warned = false;

  constructor() {
    this.canvas.addEventListener('webglcontextlost', event => {
      // Allow restoration; initialize fresh resources on the next usable frame.
      event.preventDefault();
      this.program = undefined;
    });
  }

  private initialize(gl: WebGLRenderingContext) {
    const program = gl.createProgram();
    if (!program) throw new Error('Unable to create blend mode shader program');
    for (const [type, code] of [
      [gl.VERTEX_SHADER, vertexSource],
      [gl.FRAGMENT_SHADER, fragmentSource]
    ] as const) {
      const shader = gl.createShader(type);
      if (!shader) throw new Error('Unable to create blend mode shader');
      gl.shaderSource(shader, code);
      gl.compileShader(shader);
      if (!gl.getShaderParameter(shader, gl.COMPILE_STATUS)) {
        throw new Error(`Unable to compile blend mode shader: ${gl.getShaderInfoLog(shader)}`);
      }
      gl.attachShader(program, shader);
      gl.deleteShader(shader);
    }
    gl.linkProgram(program);
    if (!gl.getProgramParameter(program, gl.LINK_STATUS)) {
      throw new Error(`Unable to link blend mode shader: ${gl.getProgramInfoLog(program)}`);
    }
    gl.useProgram(program);
    const vertices = gl.createBuffer();
    if (!vertices) throw new Error('Unable to create blend mode vertex buffer');
    gl.bindBuffer(gl.ARRAY_BUFFER, vertices);
    gl.bufferData(gl.ARRAY_BUFFER, FULLSCREEN_QUAD_VERTICES, gl.STATIC_DRAW);
    const position = gl.getAttribLocation(program, 'position');
    gl.enableVertexAttribArray(position);
    gl.vertexAttribPointer(
      position,
      POSITION_COMPONENT_COUNT,
      gl.FLOAT,
      false,
      PACKED_VERTEX_STRIDE_BYTES,
      POSITION_OFFSET_BYTES
    );
    this.textures = [SOURCE_TEXTURE_UNIT, BACKDROP_TEXTURE_UNIT].map(unit => {
      const texture = gl.createTexture();
      if (!texture) throw new Error('Unable to create blend mode texture');
      gl.activeTexture(gl.TEXTURE0 + unit);
      gl.bindTexture(gl.TEXTURE_2D, texture);
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.NEAREST);
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.NEAREST);
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
      return texture;
    });
    gl.uniform1i(gl.getUniformLocation(program, 'source'), SOURCE_TEXTURE_UNIT);
    gl.uniform1i(gl.getUniformLocation(program, 'backdrop'), BACKDROP_TEXTURE_UNIT);
    // Canvas pixels start at the top-left; WebGL texture coordinates start below.
    gl.pixelStorei(gl.UNPACK_FLIP_Y_WEBGL, true);
    this.program = program;
  }

  /**
   * left/top are backing-store pixels, not Fabric object or CSS coordinates.
   * Returns false only when the caller should use its CPU compatibility path.
   */
  render(
    ctx: CanvasRenderingContext2D,
    source: HTMLCanvasElement,
    mode: BlendMode.Add | BlendMode.Subtract,
    left: number,
    top: number
  ): boolean {
    const gl = this.gl;
    const { width, height } = source;
    if (
      !gl ||
      gl.isContextLost() ||
      width > gl.getParameter(gl.MAX_TEXTURE_SIZE) ||
      height > gl.getParameter(gl.MAX_TEXTURE_SIZE)
    ) {
      if (!this.warned) {
        console.warn('GPU blend rendering unavailable; using pixel blending');
        this.warned = true;
      }
      if (gl?.isContextLost()) this.program = undefined;
      return false;
    }
    if (!this.program) this.initialize(gl);
    if (this.canvas.width !== width) this.canvas.width = width;
    if (this.canvas.height !== height) this.canvas.height = height;
    if (this.backdrop.width !== width) this.backdrop.width = width;
    if (this.backdrop.height !== height) this.backdrop.height = height;
    const backdrop = this.backdrop.getContext('2d');
    if (!backdrop) throw new Error('Unable to create blend backdrop context');
    // Copy only the affected destination region, replacing any previous alpha.
    backdrop.globalCompositeOperation = 'copy';
    backdrop.drawImage(ctx.canvas, left, top, width, height, 0, 0, width, height);

    for (const [unit, image] of [source, this.backdrop].entries()) {
      gl.activeTexture(gl.TEXTURE0 + unit);
      gl.bindTexture(gl.TEXTURE_2D, this.textures[unit]);
      gl.texImage2D(gl.TEXTURE_2D, BASE_MIPMAP_LEVEL, gl.RGBA, gl.RGBA, gl.UNSIGNED_BYTE, image);
    }
    gl.viewport(0, 0, width, height);
    const shaderMode = mode === BlendMode.Add ? SHADER_MODE_ADD : SHADER_MODE_SUBTRACT;
    gl.uniform1i(gl.getUniformLocation(this.program!, 'mode'), shaderMode);
    gl.drawArrays(gl.TRIANGLE_STRIP, FIRST_VERTEX, QUAD_VERTEX_COUNT);
    ctx.save();
    try {
      // Pixels are already transformed; retain the live opacity and clip only.
      ctx.resetTransform();
      ctx.drawImage(this.canvas, left, top);
    } finally {
      ctx.restore();
    }
    return true;
  }
}
